import React, { useState } from 'react';
import { mutate as swrMutate } from 'swr';
import GenericModulePage from '../../../components/generic/GenericModulePage';
import { initializeModule } from '../../../utils/configInit';
import { approvalRequestsConfig } from './approvalRequestsConfig';
import { createApprovalRequestsValidationSchema } from './approvalRequestsValidation';
import { useCancelApprovalRequest } from '../hooks/useApprovalRequests';
import { ConfirmationDialog } from '../../../components/ui';

/**
 * Initialize the Approval Requests module using generic utilities
 */
const { config: initializedConfig } = initializeModule(
  approvalRequestsConfig,
  createApprovalRequestsValidationSchema
);

/**
 * Custom ApprovalRequestsPage component with cancel handler
 */
const ApprovalRequestsPage = () => {
  const cancelApprovalRequest = useCancelApprovalRequest();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [approvalToCancel, setApprovalToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancel = (approval) => {
    if (!approval || !approval.id) {
      console.error('Cannot cancel: approval or approval.id is missing');
      return;
    }

    setApprovalToCancel(approval);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!approvalToCancel || !approvalToCancel.id) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancelApprovalRequest(approvalToCancel.id, 'Cancelled by requester');
      
      // Invalidate SWR cache to refresh the data using config endpoints
      if (initializedConfig.api?.swrInvalidation) {
        if (initializedConfig.api.swrInvalidation.mainList) {
          await swrMutate(initializedConfig.api.swrInvalidation.mainList);
        }
        if (initializedConfig.api.swrInvalidation.myRequests) {
          await swrMutate(initializedConfig.api.swrInvalidation.myRequests);
        }
      }
      
      setCancelDialogOpen(false);
      setApprovalToCancel(null);
    } catch (error) {
      console.error('Error cancelling approval request:', error);
      alert(`Failed to cancel approval request: ${error.message}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCloseCancelDialog = () => {
    if (!isCancelling) {
      setCancelDialogOpen(false);
      setApprovalToCancel(null);
    }
  };

  // Add custom handlers to config
  const configWithHandlers = {
    ...initializedConfig,
    customHandlers: {
      ...initializedConfig.customHandlers,
      onCancel: handleCancel,
    },
  };

  return (
    <>
      <GenericModulePage config={configWithHandlers} />
      <ConfirmationDialog
        isOpen={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        onConfirm={handleConfirmCancel}
        title="Cancel Approval Request"
        message={`Are you sure you want to cancel approval request "${approvalToCancel?.code || approvalToCancel?.title || 'this request'}"?`}
        details={approvalToCancel ? {
          'Request Code': approvalToCancel.code || 'N/A',
          'Title': approvalToCancel.title || 'N/A',
          'Status': approvalToCancel.status?.name || approvalToCancel.status || 'N/A',
        } : null}
        warningMessage="This action cannot be undone. Only pending requests can be cancelled."
        confirmText="Cancel Request"
        cancelText="Keep Request"
        confirmVariant="destructive"
        isLoading={isCancelling}
      />
    </>
  );
};

ApprovalRequestsPage.displayName = 'ApprovalRequestsPage';

export default ApprovalRequestsPage;
